import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider } from "@/lib/auth";
import { DemoModeProvider } from "@/lib/demoMode";
import { ThemeProvider } from "@/lib/theme";
import { PersonaProvider } from "@/lib/persona";
import { NoamToaster } from "@/lib/notify";
import ProtectedRoute from "@/components/ProtectedRoute";
import OnboardingGate from "@/components/OnboardingGate";

import PublicLayout from "@/components/PublicLayout";
import Platform from "@/pages/public/Platform";
import SaaSLayout from "@/components/SaaSLayout";

import Home from "@/pages/public/Home";
import Pourquoi from "@/pages/public/Pourquoi";
import Camp from "@/pages/public/Camp";
import Contact from "@/pages/public/Contact";
import RequestAccess from "@/pages/public/RequestAccess";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import UltraAdmin from "@/pages/UltraAdmin";
import PolitiqueConfidentialite from "@/pages/PolitiqueConfidentialite";
import PublicShare from "@/pages/PublicShare";
import GraphExplorer from "@/pages/saas/GraphExplorer";
import Processus from "@/pages/saas/Processus";
import TwinLab from "@/pages/saas/TwinLab";
import OrgModel from "@/pages/saas/OrgModel";
import OnboardingPilote from "@/pages/saas/OnboardingPilote";
import AuthCallback from "@/pages/AuthCallback";

/* Retour Emergent Google Auth : le session_id arrive en fragment d'URL.
 * Détection SYNCHRONE au rendu (useLocation, pas window.location — réactivité)
 * pour traiter le session_id AVANT les routes protégées. */
function HashAuthGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

import Dashboard from "@/pages/saas/Dashboard";
import Feed from "@/pages/saas/Feed";
import Executive from "@/pages/saas/Executive";
import CommentFonctionneNoam from "@/pages/saas/CommentFonctionneNoam";
import Aujourdhui from "@/pages/saas/Aujourdhui";
import Score from "@/pages/saas/Score";
import Leads from "@/pages/saas/Leads";
import RH from "@/pages/saas/RH";
import Financement from "@/pages/saas/Financement";
import FinancialOverview from "@/pages/saas/FinancialOverview";
import Origination from "@/pages/saas/Origination";
import Risques from "@/pages/saas/Risques";
import GrcMatrix from "@/pages/saas/GrcMatrix";
import Gouvernance from "@/pages/saas/Gouvernance";
import RiskDetail from "@/pages/saas/RiskDetail";
import Ontologie from "@/pages/saas/Ontologie";
// V23.15.7 — Simplification : on route directement vers IntelligenceV2
// (le mode "Raisonnement V2" était de toute façon le sous-onglet par défaut
// et il intègre tout ce qui était utile de l'ancien chat classique).
// L'ancien `Intelligence.jsx` reste dans le repo en cas de besoin de revert.
import Intelligence from "@/pages/saas/IntelligenceV2";
import Assistant from "@/pages/saas/Assistant";
import Settings from "@/pages/saas/Settings";
import Facturation from "@/pages/saas/Facturation";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import SettingsAgents from "@/pages/saas/SettingsAgents";
import ContextSyncJournal from "@/pages/saas/ContextSyncJournal";
import SettingsMultipliers from "@/pages/saas/SettingsMultipliers";
import Audits from "@/pages/saas/Audits";
import Normes from "@/pages/saas/Normes";
import Controles from "@/pages/saas/Controles";
import Incidents from "@/pages/saas/Incidents";
import Alertes from "@/pages/saas/Alertes";
import Fournisseurs from "@/pages/saas/Fournisseurs";
import PlansAction from "@/pages/saas/PlansAction";
import MesFiltres from "@/pages/saas/MesFiltres";
import WorkspaceUsers from "@/pages/saas/WorkspaceUsers";
import SourcesCapital from "@/pages/saas/SourcesCapital";
import Analyses from "@/pages/saas/Analyses";
import Doctrine from "@/pages/saas/Doctrine";
import ProblemGravity from "@/pages/saas/ProblemGravity";
import UtilityMapping from "@/pages/saas/UtilityMapping";
import OperationalGravity from "@/pages/saas/OperationalGravity";
import Chat from "@/pages/saas/Chat";
import Bouteille from "@/pages/saas/Bouteille";
import Cercle from "@/pages/saas/Cercle";
import ChatInterne from "@/pages/saas/ChatInterne";
import Negociation from "@/pages/saas/Negociation";
import Chainage from "@/pages/saas/Chainage";
import Immobilier from "@/pages/saas/Immobilier";
import Courtage from "@/pages/saas/Courtage";
import Deals from "@/pages/saas/Deals";
import Deal360 from "@/pages/saas/Deal360";
import DealSystemHome from "@/pages/saas/DealSystemHome";
import DealPipeline from "@/pages/saas/DealPipeline";
import DecisionJournal from "@/pages/saas/DecisionJournal";
import Workflows from "@/pages/saas/Workflows";
import RealValidation from "@/pages/saas/RealValidation";
import ProcessIntelligence from "@/pages/saas/ProcessIntelligence";
import BoardPack from "@/pages/saas/BoardPack";
import Annuaire from "@/pages/saas/Annuaire";
import Documents from "@/pages/saas/Documents";
import Cartographie360 from "@/pages/saas/Cartographie360";
import Cartographie360Index from "@/pages/saas/Cartographie360Index";
import StrategicRelationships from "@/pages/saas/StrategicRelationships";
import StrategicRelationship360 from "@/pages/saas/StrategicRelationship360";
import Instruments from "@/pages/saas/Instruments";
import GraphNoeudsPage from "@/pages/saas/GraphNoeudsPage";
import Gravites from "@/pages/saas/groups/Gravites";
import RisqueConformite from "@/pages/saas/groups/RisqueConformite";
import Capital from "@/pages/saas/groups/Capital";
import Cartographies from "@/pages/saas/groups/Cartographies";
import OriginationHub from "@/pages/saas/groups/OriginationHub";
import ViewsManager from "@/pages/saas/ViewsManager";
import UrlScanner from "@/pages/saas/UrlScanner";
import Metacognition from "@/pages/saas/Metacognition";
import AgenticHome from "@/pages/saas/AgenticHome";
import { LLMJobsProvider } from "@/components/LLMJobsTray";
import { ActiveViewProvider } from "@/lib/useActiveView";
import Asymetries from "@/pages/saas/Asymetries";
import Radar from "@/pages/saas/Radar";
import Brvm from "@/pages/saas/Brvm";
import BriefsLibrary from "@/pages/saas/BriefsLibrary";
import Digests from "@/pages/saas/Digests";
import AvantApres from "@/pages/saas/AvantApres";
import Historique from "@/pages/saas/Historique";
import CausalSimulator from "@/pages/saas/CausalSimulator";
import MiroirDuMatin from "@/pages/saas/MiroirDuMatin";
// D2 — Hub 3+1 (additif)
import Hub from "@/pages/saas/Hub";
import { SystemsProvider, HubIndexRedirect } from "@/lib/systems";
import SystemGuard from "@/components/SystemGuard";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
          <NoamToaster />
          <HashAuthGate>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="/pourquoi" element={<Pourquoi />} />
              <Route path="/why" element={<Pourquoi />} />
              <Route path="/camp" element={<Camp />} />
              <Route path="/noam-camp" element={<Camp />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/request-access" element={<RequestAccess />} />
              {/* Legacy public pages — redirect to home (kept as fallback) */}
              <Route path="/plateforme" element={<Navigate to="/" replace />} />
              <Route path="/conseil" element={<Navigate to="/" replace />} />
              <Route path="/secteurs" element={<Navigate to="/" replace />} />
              <Route path="/equipe" element={<Navigate to="/" replace />} />
              <Route path="/qualifier" element={<Navigate to="/" replace />} />
            </Route>

            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* V23.15.67 — Politique de confidentialité publique */}
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/privacy" element={<Navigate to="/politique-confidentialite" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/share/:token" element={<PublicShare />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <SystemsProvider>
                  <PersonaProvider>
                    <ActiveViewProvider>
                      <LLMJobsProvider>
                        <SaaSLayout />
                      </LLMJobsProvider>
                    </ActiveViewProvider>
                  </PersonaProvider>
                  </SystemsProvider>
                </ProtectedRoute>
              }
            >
              <Route element={<OnboardingGate />}>
                <Route index element={<HubIndexRedirect />} />
                <Route path="hub" element={<Hub />} />
                <Route path="relations" element={<StrategicRelationships />} />
                <Route path="relations/:id" element={<StrategicRelationship360 />} />
                <Route path="instruments" element={<Instruments />} />
                <Route path="noam" element={<AgenticHome />} />
                <Route path="miroir" element={<MiroirDuMatin />} />
                <Route path="aujourdhui" element={<Aujourdhui />} />
                <Route path="comment-ca-marche" element={<CommentFonctionneNoam />} />
                <Route path="executive" element={<Executive />} />
                <Route path="score" element={<Score />} />
                <Route path="leads" element={<Leads />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="rh" element={<RH />} />
                <Route path="financement" element={<Financement />} />
                <Route path="financial-overview" element={<FinancialOverview />} />
                <Route path="origination" element={<Navigate to="/app/origination-hub" replace />} />
                <Route path="sources-capital" element={<SourcesCapital />} />
                <Route path="risques" element={<SystemGuard system="governance"><Risques /></SystemGuard>} />
                <Route path="grc-matrix" element={<SystemGuard system="governance"><GrcMatrix /></SystemGuard>} />
                <Route path="risques/:id" element={<SystemGuard system="governance"><RiskDetail /></SystemGuard>} />
                <Route path="gouvernance" element={<SystemGuard system="governance"><Gouvernance /></SystemGuard>} />
                <Route path="processus" element={<SystemGuard system="governance"><Processus /></SystemGuard>} />
                <Route path="twin-lab" element={<TwinLab />} />
                <Route path="org-model" element={<OrgModel />} />
                <Route path="onboarding-pilote" element={<OnboardingPilote />} />
                <Route path="gouvernance/:tab" element={<SystemGuard system="governance"><Gouvernance /></SystemGuard>} />
                <Route path="ontologie" element={<Ontologie />} />
                <Route path="intelligence" element={<Intelligence />} />
                <Route path="configurer" element={<Assistant />} />
                <Route path="settings" element={<Settings />} />
                <Route path="facturation" element={<Facturation />} />
                <Route path="settings/agents" element={<SettingsAgents />} />
                <Route path="settings/context-sync" element={<ContextSyncJournal />} />
                <Route path="settings/multipliers" element={<SettingsMultipliers />} />
                <Route path="audits" element={<Audits />} />
                <Route path="normes" element={<Normes />} />
                <Route path="positionnement" element={<Navigate to="/app/origination-hub/positionnement" replace />} />
                <Route path="controles" element={<Controles />} />
                <Route path="incidents" element={<Incidents />} />
                <Route path="alertes" element={<Alertes />} />
                <Route path="fournisseurs" element={<Fournisseurs />} />
                <Route path="plans" element={<PlansAction />} />
                <Route path="cartographie/:type/:id" element={<Cartographie360 />} />
                <Route path="cartographies/index" element={<Cartographie360Index />} />
                <Route path="graphe-noeuds" element={<GraphNoeudsPage />} />
                <Route path="gravites" element={<Gravites />} />
                <Route path="gravites/:tab" element={<Gravites />} />
                <Route path="risque-conformite" element={<RisqueConformite />} />
                <Route path="risque-conformite/:tab" element={<RisqueConformite />} />
                <Route path="capital" element={<Capital />} />
                <Route path="capital/:tab" element={<Capital />} />
                <Route path="cartographies" element={<Cartographies />} />
                <Route path="cartographies/:tab" element={<Cartographies />} />
                <Route path="origination-hub" element={<OriginationHub />} />
                <Route path="origination-hub/:tab" element={<OriginationHub />} />
                <Route path="settings/views" element={<ViewsManager />} />
                <Route path="url-scanner" element={<UrlScanner />} />
                <Route path="metacognition" element={<ProtectedRoute superAdminOnly><Metacognition /></ProtectedRoute>} />
                <Route path="filtres" element={<MesFiltres />} />
                <Route path="analyses" element={<Analyses />} />
                <Route path="doctrine" element={<Doctrine />} />
                <Route path="trend" element={<Navigate to="/app/radar?tab=veille" replace />} />
                <Route path="feed" element={<Feed />} />
                <Route path="problem-gravity" element={<ProblemGravity />} />
                <Route path="utility-mapping" element={<UtilityMapping />} />
                <Route path="operational-gravity" element={<OperationalGravity />} />
                <Route path="chat" element={<Chat />} />
                <Route path="bouteille-a-la-mer" element={<Navigate to="/app/cercle" replace />} />
                <Route path="cercle" element={<Cercle />} />
                <Route path="chat-interne" element={<ChatInterne />} />
                <Route path="negociation" element={<Negociation />} />
                <Route path="chainage" element={<Chainage />} />
                <Route path="immobilier" element={<SystemGuard system="deal"><Immobilier /></SystemGuard>} />
                <Route path="courtage" element={<SystemGuard system="deal"><Courtage /></SystemGuard>} />
                <Route path="deal-system" element={<SystemGuard system="deal"><DealSystemHome /></SystemGuard>} />
                <Route path="decisions" element={<DecisionJournal />} />
                <Route path="workflows" element={<Workflows />} />
                <Route path="real-validation" element={<RealValidation />} />
                <Route path="process" element={<ProcessIntelligence />} />
                <Route path="board-pack" element={<BoardPack />} />
                <Route path="deal-pipeline" element={<SystemGuard system="deal"><DealPipeline /></SystemGuard>} />
                <Route path="deals" element={<SystemGuard system="deal"><Deals /></SystemGuard>} />
                <Route path="deals/:id" element={<SystemGuard system="deal"><Deal360 /></SystemGuard>} />
                <Route path="annuaire" element={<Annuaire />} />
                <Route path="graph" element={<GraphExplorer />} />
                <Route path="documents" element={<Documents />} />
                <Route path="asymetries" element={<Asymetries />} />
                <Route path="radar" element={<Radar />} />
                <Route path="brvm" element={<SystemGuard system="financial_markets"><Brvm /></SystemGuard>} />
                <Route path="briefs-library" element={<BriefsLibrary />} />
                <Route path="digests" element={<Digests />} />
                <Route path="avant-apres" element={<AvantApres />} />
                <Route path="historique" element={<Historique />} />
                <Route path="simulator" element={<CausalSimulator />} />
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute adminOnly>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute>
                      <WorkspaceUsers />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>

            <Route
              path="/ultra-admin"
              element={
                <ProtectedRoute superAdminOnly>
                  <UltraAdmin />
                </ProtectedRoute>
              }
            />

            {/* PUBLIC DEMO — same components, no auth, with banner overlay */}
            <Route
              path="/demo"
              element={
                <DemoModeProvider value={true}>
                  <SaaSLayout />
                </DemoModeProvider>
              }
            >
              <Route index element={<Navigate to="/demo/executive" replace />} />
              <Route path="executive" element={<Executive />} />
              <Route path="score" element={<Score />} />
              <Route path="leads" element={<Leads />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="rh" element={<RH />} />
              <Route path="financement" element={<Financement />} />
                <Route path="financial-overview" element={<FinancialOverview />} />
              <Route path="origination" element={<Origination />} />
              <Route path="risques" element={<Risques />} />
              <Route path="grc-matrix" element={<GrcMatrix />} />
              <Route path="risques/:id" element={<RiskDetail />} />
              <Route path="ontologie" element={<Ontologie />} />
              <Route path="intelligence" element={<Intelligence />} />
              <Route path="configurer" element={<Assistant />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </HashAuthGate>
        </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
